<script>
import ContentGroup from '@/components/v3/widgets/ContentGroup';
import CollapseContent from '@/components/v3/base/CollapseContent';
import CustomImage from '@/components/card/CustomImage';

export default {
  name: 'ExperienceGroup',
  components: {
    ContentGroup,
    CollapseContent,
    CustomImage,
  },

  props: {
    experiences: {
      type: [Array, String],
      default: () => [],
    },

    title: {
      type: String,
      default: 'Experience',
    },

    type: {
      type: String,
      default: 'experience',
    },

    titleOfViewmore: String,
    limit: {
      type: Number,
      default: 3,
    },
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    experiencesItems() {
      if (this.type !== 'skills') return this.experiences;
      const items = [];
      this.experiences.forEach((item) => {
        const arr = item.name.split(' • ');
        if (arr.length > 1) {
          arr.forEach((val) => {
            items.push({ name: val });
          });
        } else items.push(item);
      });

      return items;
    },

    itemsDisplay() {
      if (!Array.isArray(this.experiencesItems)) return '';
      return this.isOpen
        ? this.experiencesItems
        : this.experiencesItems.filter((item, i) => i < this.limit);
    },
  },

  methods: {
    getCompanyLogoUrl(experience) {
      if (
        experience.companyLogo &&
        !experience.companyLogo.startsWith('data:image')
      ) {
        return experience.companyLogo;
      }

      if (
        experience.logo_url &&
        !experience.logo_url.startsWith('data:image')
      ) {
        return experience.logo_url;
      }
      return 'https://www.linkedin.com/sc/h/9rxpcj33wtsq3w7nxb3u3ku6e';
    },

    getEducationLogoUrl(education) {
      if (
        education.schoolLogo &&
        !education.schoolLogo.startsWith('data:image')
      ) {
        return education.schoolLogo;
      }
      if (education.logo_url && !education.logo_url.startsWith('data:image')) {
        return education.logo_url;
      }
      return 'https://www.linkedin.com/sc/h/cr28v9rbzul6ucgef2vktxolt';
    },

    getLogoUrl(experience) {
      if (this.type === 'education')
        return this.getEducationLogoUrl(experience);
      return this.getCompanyLogoUrl(experience);
    },

    hexToBase64(str) {
      return window.btoa(
        String.fromCharCode.apply(
          null,
          str
            .replace(/\r|\n/g, '')
            .replace(/([\da-fA-F]{2}) ?/g, '0x$1 ')
            .replace(/ +$/, '')
            .split(' ')
        )
      );
    },

    getStartDate(experience) {
      if (experience.start_date) return experience.start_date;
      if (experience.starts_at) return experience.starts_at;
      return '';
    },

    getEndDate(experience) {
      if (experience.end_date) return experience.end_date;
      if (experience.ends_at) return experience.ends_at;
      return '';
    },

    getEducationDegree(education) {
      if (education.degree) return education.degree;
      if (education.degree_name) return education.degree_name;
      return '';
    },

    getEducationMajor(education) {
      if (education.major) return education.major;
      if (education.field_of_study) return education.field_of_study;
      return '';
    },

    getLogoUrlFromName(education) {
      if (education.school) {
        return `https://ui-avatars.com/api/?background=ffffff&color=384955&name=${education.school}`;
      }
      if (education.school_profile_url) {
        return `https://ui-avatars.com/api/?background=ffffff&color=384955&name=${education.school_profile_url}`;
      }
      return 'https://www.linkedin.com/sc/h/cr28v9rbzul6ucgef2vktxolt';
    },
  },
};
</script>
<template>
  <ContentGroup
    :title="title"
    :titleOfViewmore="titleOfViewmore"
    :isFull="isOpen"
    :count="Array.isArray(experiencesItems) ? experiencesItems.length : 0"
    :limit="limit"
    @show="isOpen = $event"
  >
    <div v-if="Array.isArray(experiences) && type !== 'skills'">
      <div
        v-for="(experience, index) in itemsDisplay"
        :key="index"
        class="tw-pl-[42px] tw-relative tw-mt-4"
        :class="{ '!tw-mt-0': index === 0 }"
      >
        <div class="tw-pt-1 tw-absolute tw-top-0 tw-left-0">
          <a
            :href="experience.companyUrl"
            target="_blank"
            class="tw-w-8 tw-h-8 tw-block"
          >
            <img
              v-if="experience.companyLogoBinary && !experience.companyLogo"
              :src="`data:image/jpeg;base64,${hexToBase64('')}`"
              class="tw-w-full tw-h-full tw-object-cover"
            />
            <div v-else>
              <CustomImage
                :src="getLogoUrl(experience)"
                class="tw-w-full tw-h-full tw-object-cover"
                :emptySrc="getLogoUrlFromName(experience)"
              />
            </div>
          </a>
        </div>
        <div class="">
          <div class="">
            {{ experience.title || experience.school }}
            <span v-if="experience.company">at&nbsp;</span>
            <strong class="tw-font-semibold">{{ experience.company }}</strong>
          </div>
        </div>
        <div class="tw-text-sm tw-text-gray-700 tw-leading-5">
          {{ getStartDate(experience) }} -
          {{ !!getEndDate(experience) ? getEndDate(experience) : 'Present' }}
          {{ experience.tenure ? `· ${experience.tenure}` : '' }}
          <span v-if="type === 'education'"
            >{{ getEducationDegree(experience) ? '|' : '' }}
            {{ getEducationDegree(experience) }},
            {{ getEducationMajor(experience) }}</span
          >
          <span v-else>
            {{ experience.location ? '|' : '' }}
            {{ experience.location }}</span
          >
        </div>

        <div
          v-if="experience.description"
          class="tw-mt-2 tw-text-sm tw-leading-5 tw-word-break"
        >
          <CollapseContent :content="experience.description" :limit="50" />
        </div>
      </div>
    </div>
    <div v-else class="tw-text-sm tw-leading-5">
      <div v-if="type !== 'skills'">
        <CollapseContent :content="experiences" />
      </div>
      <div v-else class="tw-flex tw-flex-wrap tw-gap-3">
        <div
          v-for="(experience, i) in itemsDisplay"
          :key="i"
          class="tw-inline-flex tw-gap-1 tw-border tw-border-solid tw-items-center tw-rounded-full tw-justify-between tw-text-sm tw-leading-5 tw-px-3 tw-font-bold tw-border-gray-400 tw-bg-white"
        >
          <div class="tw-my-1.5">
            {{ experience.name }}
          </div>
        </div>
      </div>
    </div>
  </ContentGroup>
</template>
